import isEqual from 'lodash/isEqual';
import { put, select, call } from 'lib/redux';
import getReportAnalyticsService from 'services/getReportAnalytics.service';
import getReportAnalyticsCountService from 'services/getReportAnalyticsCount.service';
import getStatefarmDashboardService from 'services/getStatefarmDashboard.service';
import { getInitParametersSaga, getProductCatalogSaga } from 'features/userData/UserData.saga';
import {
  FETCH_REPORT_ANALYTICS_LOADING, SET_REPORT_ANALYTICS_PAGE_OPTIONS, RESET_REPORT_ANALYTICS_FILTERS, APPLY_REPORT_ANALYTICS_FILTERS,
  ADD_REPORT_ANALYTICS_PRODUCTS_LIST, APPLY_REPORT_ANALYTICS_FILTERS_FROM_URL,
  SET_ANALYTICS_REPORTS_COUNT,
  FETCH_STATEFARM_DASHBOARD_LOADING,
  FETCH_STATEFARM_DASHBOARD_SUCCESS,
  FETCH_STATEFARM_DASHBOARD_ERROR,
  UPDATE_LOCAL_STATEFARM_FILTER,
} from 'constants/ActionTypes';
import history from 'store/CreateHistory';
import { ANALYTICS_DASHBOARD_ROUTE } from 'constants/RouteConstants';
import { logError } from 'utils/logger.utils';
import { fetchReportAnalyticsFailure, fetchReportAnalyticsSuccess } from './AnalyticsDashboard.actions';
import {
  getProductsListForReportAnalytics, getQueryParamsFromURL, getRequestParamsForReportAnalytics, setQueryValuesToCustomURLParams,
} from './AnalyticsDashboard.utils';

export function* fetchStatefarmDashboardSaga() {
  try {
    yield put({ type: FETCH_STATEFARM_DASHBOARD_LOADING });
    const appliedReportAnalyticsFilters = yield select((state) => state.analyticsDashboard.appliedReportAnalyticsFilters);
    const response = yield call(getStatefarmDashboardService, appliedReportAnalyticsFilters);
    if (response.ok) {
      yield put({ type: FETCH_STATEFARM_DASHBOARD_SUCCESS, data: response.json });

      const stateFarmLocalFilters = yield select((state) => state.analyticsDashboard.stateFarmLocalFilters);
      const stateFarmAppliedFilters = yield select((state) => state.analyticsDashboard.stateFarmAppliedFilters);
      if (!isEqual(stateFarmLocalFilters, stateFarmAppliedFilters)) {
        Object.keys(stateFarmAppliedFilters).forEach((key) => {
          put({
            type: UPDATE_LOCAL_STATEFARM_FILTER,
            key,
            value: stateFarmAppliedFilters[key],
          });
        });
      }
    } else {
      yield put({ type: FETCH_STATEFARM_DASHBOARD_ERROR, error: response.json?.errorDescription });
    }
  } catch (error) {
    yield put({ type: FETCH_STATEFARM_DASHBOARD_ERROR, error: error.message });
  }
}

export function* fetchReportAnalyticsCountSaga() {
  const {
    appliedReportAnalyticsFilters, productsList,
  } = yield select(({ analyticsDashboard }) => analyticsDashboard);
  const orgId = yield select(({ userData }) => userData.initParameters.orgNodeId);

  const requestParams = getRequestParamsForReportAnalytics(appliedReportAnalyticsFilters, productsList);
  const queryParams = Object.keys(requestParams).map((key) => `${key}=${requestParams[key]}`).join('&');

  const myevEnterpriseAnalyticsChange = yield select(({ featureFlag }) => featureFlag.myevEnterpriseAnalyticsChange);

  const countResponse = yield call(getReportAnalyticsCountService, queryParams, myevEnterpriseAnalyticsChange ? 149056 : orgId);
  if (!countResponse.ok) {
    const error = new Error(countResponse.json.errorDescription);
    error.errorCode = countResponse.json.errorCode;
    throw error;
  }
  const { json: { count } } = countResponse;
  yield put({ type: SET_ANALYTICS_REPORTS_COUNT, count });
}

export function* fetchReportAnalyticsSaga() {
  try {
    yield put({ type: FETCH_REPORT_ANALYTICS_LOADING });
    const queryParamsFromURL = getQueryParamsFromURL();
    yield call(getInitParametersSaga);
    yield call(getProductCatalogSaga); // #genai include internals for analytics product filter
    yield call(fetchReportAnalyticsCountSaga);
    const products = yield select(({ userData }) => userData.productCatalog);
    const productsList = getProductsListForReportAnalytics(products);
    yield put({ type: ADD_REPORT_ANALYTICS_PRODUCTS_LIST, productsList });
    const orgId = yield select(({ userData }) => userData.initParameters.orgNodeId);
    const {
      pageNumber, pageSize, appliedReportAnalyticsFilters, reportsCount,
    } = yield select(({ analyticsDashboard }) => (analyticsDashboard));
    const fetchAllReports = history.location.pathname === ANALYTICS_DASHBOARD_ROUTE;
    const pageNumberToFetch = fetchAllReports ? 1 : pageNumber;
    const pageSizeToFetch = fetchAllReports ? reportsCount : pageSize;

    const updatedAppliedReportAnalyticsFilters = {
      ...appliedReportAnalyticsFilters,
      ...queryParamsFromURL,
      selectedProduct: Number(queryParamsFromURL?.selectedProduct || appliedReportAnalyticsFilters?.selectedProduct),
    };

    yield put({ type: APPLY_REPORT_ANALYTICS_FILTERS_FROM_URL, filters: updatedAppliedReportAnalyticsFilters });

    const requestParams = getRequestParamsForReportAnalytics(updatedAppliedReportAnalyticsFilters, productsList, pageNumberToFetch, pageSizeToFetch);
    const queryParams = Object.keys(requestParams).map((key) => `${key}=${requestParams[key]}`).join('&');
    const myevEnterpriseAnalyticsChange = yield select(({ featureFlag }) => featureFlag.myevEnterpriseAnalyticsChange);
    const currentPath = history.location.pathname;
    setQueryValuesToCustomURLParams(currentPath, updatedAppliedReportAnalyticsFilters, pageNumberToFetch, pageSizeToFetch);

    const response = yield call(getReportAnalyticsService, queryParams, myevEnterpriseAnalyticsChange ? 149056 : orgId);
    if (!response.ok) {
      const error = new Error(response.json.errorDescription);
      error.errorCode = response.json.errorCode;
      logError('Failed to fetch report analytics', {
        error: error.message,
        errorCode: error.errorCode,
        orgId,
      });
      throw error;
    }
    yield put(fetchReportAnalyticsSuccess(response.json));
  } catch (error) {
    logError('Error in fetchReportAnalyticsSaga', {
      error: error.message,
      errorCode: error.errorCode,
    });
    yield put(fetchReportAnalyticsFailure());
  }
}

export function* updateReportAnalyticsPageOptionsSaga({ pageOptions }) {
  try {
    yield put({ type: SET_REPORT_ANALYTICS_PAGE_OPTIONS, pageOptions });

    const { pageNumber, pageSize } = yield select(({ analyticsDashboard }) => analyticsDashboard);
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (pageNumber) urlSearchParams.set('pageNo', pageNumber);
    if (pageSize) urlSearchParams.set('rows', pageSize);

    yield call(fetchReportAnalyticsSaga);
  } catch (error) {
    yield put(fetchReportAnalyticsFailure());
  }
}

export function* setReportAnalyticsFiltersSaga({ reset }) {
  if (reset) {
    yield put({ type: RESET_REPORT_ANALYTICS_FILTERS });
    history.replace(history.location.pathname);
  } else {
    yield put({ type: APPLY_REPORT_ANALYTICS_FILTERS });
    const {
      pageNumber, pageSize, appliedReportAnalyticsFilters, reportsCount,
    } = yield select(({ analyticsDashboard }) => (analyticsDashboard));
    const fetchAllReports = history.location.pathname === ANALYTICS_DASHBOARD_ROUTE;
    const pageNumberToFetch = fetchAllReports ? 1 : pageNumber;
    const pageSizeToFetch = fetchAllReports ? reportsCount : pageSize;
    const currentPath = history.location.pathname;
    setQueryValuesToCustomURLParams(currentPath, appliedReportAnalyticsFilters, pageNumberToFetch, pageSizeToFetch);
  }
  yield call(fetchReportAnalyticsSaga);
}
